import { QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion, domAnimation } from 'motion/react';
import { ThemeProvider } from 'next-themes';
import type { ReactNode } from 'react';

import { Toaster } from '@/components/ui/sonner';
import { BreadcrumbProvider } from '@/hooks/breadcrumb';

import { ApiError, queryClient, useOperationQuery } from './api';
import { AppSessionProvider, type ClientSession } from './next/next-auth-react';

/** The signed-in user, or null. Loads once before any page renders. */
function SessionLoader({ children }: { children: ReactNode }) {
    const me = useOperationQuery('me.get', {}, { staleTime: Infinity });
    if (me.isPending) return null;
    const user = me.data?.user;
    const session: ClientSession | null =
        me.error instanceof ApiError || !user
            ? null
            : {
                  user: {
                      id: user.id,
                      email: user.email,
                      image: user.image,
                      organizationId: me.data?.organization?.id ?? null
                  },
                  expires: ''
              };
    return (
        <AppSessionProvider session={session}>{children}</AppSessionProvider>
    );
}

/** Providers of apps/webapp/app/providers.tsx, around every route. */
export function AppRoot({ children }: { children: ReactNode }) {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider
                attribute="class"
                defaultTheme="system"
                enableSystem
                disableTransitionOnChange
            >
                <LazyMotion features={domAnimation} strict>
                    <BreadcrumbProvider>
                        <SessionLoader>{children}</SessionLoader>
                        <Toaster />
                    </BreadcrumbProvider>
                </LazyMotion>
            </ThemeProvider>
        </QueryClientProvider>
    );
}
